import React, { useCallback, useContext, useEffect, useState } from 'react';
import { notifications } from '@mantine/notifications';
import { downloadPathlossCatalog } from '../../../common/downloadPathlossCatalog';
import { makeSafeRequest } from '../../../common/makeSafeRequest';
import {
	getEmitterPathlossWarnings,
	validateRadioParameters,
} from '../../../common/pathlossValidation';
import Preloader from '../../../components/common/Preloader';
import { AppInfoContext } from '../../../components/contexts/AppInfoContext';
import SystemPage from '../../../components/system-page/SystemPage';
import { ListForcePackageData, Optional, SystemSettingsObjectType } from '../../../types/common';
import { EmitterData, FiltersData, InputData } from '../../../types/systems';
import { defaultSettings, emittersSettingsTemplate } from './defaultSettings';

const Emitters: React.FC = () => {
	const [filters, setFilters] = useState<FiltersData>();
	const [inputData, setInputData] = useState<InputData>();
	const { currentPid } = useContext(AppInfoContext);

	const initialize = useCallback(
		async (pid: number, fp: number | string, side: number | string) => {
			const [filters, inpData, pathlossCatalog] = await Promise.all([
				downloadFilters(pid, fp, side),
				downloadInputData(),
				downloadPathlossCatalog(),
			]);

			if (!filters || !inpData) {
				return false;
			}

			setFilters(filters);
			setInputData({ ...inpData, pathlossCatalog });
			return true;
		},
		[]
	);

	const downloadFilters = async (pid: number, fp: number | string, side: number | string) => {
		return await makeSafeRequest<FiltersData>('emitters/', 'GET', {
			params: { pid: pid, fp: fp, side: side },
		});
	};

	const downloadSystemSettings = async (systemId: number) => {
		return await makeSafeRequest<EmitterData>(`emitters/${systemId}/`, 'GET');
	};

	const downloadInputData = async () => {
		return await makeSafeRequest<InputData>('emitters/input-data/', 'GET');
	};

	const addEmitter = async (settings: Optional<EmitterData, 'id'>) => {
		if (!validateEmitter(settings)) return null;
		return await makeSafeRequest<EmitterData>('emitters/', 'POST', {
			payload: settings,
			onSuccessMessage: 'Emitter added successfully!',
		});
	};

	const deleteEmitter = async (settingsId: number) => {
		return await makeSafeRequest<null>(`emitters/${settingsId}/`, 'DELETE', {
			onSuccessMessage: 'Emitter deleted successfully!',
		});
	};

	const saveEmitter = async (settings: Optional<EmitterData, 'id'>) => {
		if (!validateEmitter(settings)) return null;
		return await makeSafeRequest<EmitterData>(`emitters/${settings.id}/`, 'PUT', {
			payload: settings,
			onSuccessMessage: 'Emitter saved successfully!',
		});
	};

	const validateEmitter = (settings: Optional<EmitterData, 'id'>) => {
		const errors = validateRadioParameters(settings.radio_parameters);
		if (errors.length === 0) return true;
		notifications.show({
			title: 'Invalid radio parameters',
			message: errors.join(' '),
			color: 'red',
			autoClose: 8000,
		});
		return false;
	};

	const duplicateEmitter = async (settingsId: number) => {
		return await makeSafeRequest<EmitterData>(`emitters/${settingsId}/duplicate/`, 'POST', {
			onSuccessMessage: 'Emitter duplicated successfully!',
		});
	};

	useEffect(() => {
		if (emittersSettingsTemplate && inputData && inputData.protocols) {
			(
				emittersSettingsTemplate.sections['Marks'].protocol_ids as SystemSettingsObjectType
			).data = inputData.protocols.map((protocol) => {
				return {
					value: protocol.id,
					label: protocol.protocol_json.name,
				};
			});
		}
	}, [inputData]);

	useEffect(() => {
		initialize(currentPid, '', '');
	}, [initialize, currentPid]);

	return filters && inputData ? (
		<SystemPage
			name="emitter"
			forcePackageData={filters.fps as ListForcePackageData[]}
			systemsData={filters.emitters as EmitterData[]}
			inputData={inputData}
			initializeFilters={initialize}
			downloadSystemSettings={downloadSystemSettings}
			systemSettingsTemplate={emittersSettingsTemplate}
			defaultSettings={defaultSettings}
			addSystem={addEmitter}
			saveSystem={saveEmitter}
			deleteSystem={deleteEmitter}
			duplicateSystem={duplicateEmitter}
			getWarnings={(settings) =>
				getEmitterPathlossWarnings(settings, inputData.pathlossCatalog!)
			}
		></SystemPage>
	) : (
		<Preloader></Preloader>
	);
};

export default Emitters;
